import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaEye, FaRocket, FaSatellite, FaSearchLocation, FaThermometerHalf, FaWifi } from 'react-icons/fa';
import SEO from '../SEO';
import { buildStructuredData, getAbsoluteUrl, getKeywords, getRouteSeo, siteSeo } from '../../seo/seoUtils';
import './Missions.css';

const missions = [
  {
    id: 1,
    icon: FaSatellite,
    title: 'CubeSat-1: Communications Backbone',
    description:
      'Affordable communications infrastructure for remote sensing and resilient data transmission across distributed networks.',
    year: 'Planned',
    status: 'Concept',
    tags: ['Comms payload', 'LEO · SSO', 'Rideshare target']
  },
  {
    id: 2,
    icon: FaSearchLocation,
    title: 'CubeSat-2: Space Weather Monitor',
    description:
      'Solar flare and space-weather monitoring to protect satellite operations and support real-time ground decisions.',
    year: 'Planned',
    status: 'Concept',
    tags: ['Solar monitoring', 'Space weather', 'Sensor package']
  },
  {
    id: 3,
    icon: FaEye,
    title: 'CubeSat-3: Earth Observation',
    description:
      'Environmental monitoring with multi-spectral imaging for pollution, climate, and land-use intelligence.',
    year: 'Planned',
    status: 'Design Study',
    tags: ['Imaging payload', 'Multi-spectral', 'Environmental']
  },
  {
    id: 4,
    icon: FaWifi,
    title: 'CubeSat-4: Global IoT Connectivity',
    description:
      'Satellite-enabled connectivity for underserved regions and industrial device fleets requiring low-latency coverage.',
    year: 'Planned',
    status: 'Concept',
    tags: ['IoT payload', 'Low-power comms', 'Global coverage']
  },
  {
    id: 5,
    icon: FaThermometerHalf,
    title: 'CubeSat-5: Thermal Agriculture',
    description:
      'Thermal sensing payloads supporting crop monitoring, irrigation decisions, and precision farming operations.',
    year: 'Planned',
    status: 'Research Phase',
    tags: ['Thermal sensor', 'Agriculture', 'Remote sensing']
  },
  {
    id: 6,
    icon: FaRocket,
    title: 'Mission X: Deep Space Research',
    description:
      'Long-term interplanetary research platform exploring autonomous navigation and intelligent mission control.',
    year: 'Long-term',
    status: 'Concept',
    tags: ['Deep space', 'Autonomous nav', 'Research platform']
  }
];

const summaryStats = [
  { value: '6', label: 'Mission concepts in design' },
  { value: 'LEO · SSO', label: 'Primary target orbits' },
  { value: 'Concept', label: 'Current program phase' }
];

const introVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] }
  }
};

const gridVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.05 }
  }
};

const cardVariants = {
  hidden: { opacity: 0, y: 28, scale: 0.97 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] }
  }
};

const Missions = ({ enableSEO = true }) => {
  const seo = getRouteSeo('/missions');
  const HeadingTag = enableSEO ? 'h1' : 'h2';

  return (
    <>
      {enableSEO && (
        <SEO
          title={seo.title}
          description={seo.description}
          keywords={getKeywords(seo)}
          url={getAbsoluteUrl('/missions')}
          image={siteSeo.image}
          imageAlt={siteSeo.imageAlt}
          structuredData={buildStructuredData('/missions')}
        />
      )}

      <section id="missions" className="missions-section">
        <div className="missions-section__container">
          <motion.div
            className="missions-section__intro"
            variants={introVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
          >
            <motion.span className="section-kicker" variants={fadeUp}>Mission portfolio</motion.span>
            <HeadingTag className="section-heading">
              <motion.span style={{ display: 'block' }} variants={fadeUp}>
                CubeSat missions and <span>satellite programs</span> in design.
              </motion.span>
            </HeadingTag>
            <motion.p className="section-copy" variants={fadeUp}>
              Our mission portfolio spans communications, space weather monitoring, Earth
              observation, IoT connectivity, thermal agriculture, and deep space research —
              concepts scoped for practical deployment as the vehicles mature.
            </motion.p>
            <motion.p className="missions-section__supporting-copy" variants={fadeUp}>
              See how these programs connect with our{' '}
              <Link to="/about" className="missions-section__link">
                space systems approach
              </Link>
              ,{' '}
              <Link to="/rockets" className="missions-section__link">
                launch vehicle concepts
              </Link>
              , and{' '}
              <Link to="/contact" className="missions-section__link">
                mission planning team
              </Link>
              .
            </motion.p>
          </motion.div>

          <section aria-labelledby="missions-portfolio-heading">
            <h2 id="missions-portfolio-heading" className="missions-section__sr-only">
              CubeSat and satellite mission portfolio
            </h2>
            <motion.div
              className="missions-section__grid"
              variants={gridVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-50px' }}
            >
              {missions.map((mission) => {
                const IconComponent = mission.icon;
                const statusClass = mission.status.toLowerCase().replace(/\s+/g, '-');

                return (
                  <motion.article
                    key={mission.id}
                    className="missions-section__card glass-card"
                    variants={cardVariants}
                  >
                    <div className="missions-section__card-top">
                      <div className="missions-section__icon">
                        <IconComponent aria-hidden="true" />
                      </div>

                      <div className="missions-section__meta">
                        <span className="missions-section__year">{mission.year}</span>
                        <span className={`missions-section__status missions-section__status--${statusClass}`}>
                          {mission.status}
                        </span>
                      </div>
                    </div>

                    <h3>{mission.title}</h3>
                    <p>{mission.description}</p>

                    <div className="missions-section__tags">
                      {mission.tags.map((tag) => (
                        <span key={tag}>{tag}</span>
                      ))}
                    </div>
                  </motion.article>
                );
              })}
            </motion.div>
          </section>

          <section aria-labelledby="missions-highlights-heading">
            <h2 id="missions-highlights-heading" className="missions-section__sr-only">
              Mission program highlights
            </h2>
            <motion.div
              className="missions-section__summary glass-card"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
            >
              {summaryStats.map((stat) => (
                <div key={stat.label} className="missions-section__summary-item">
                  <strong>{stat.value}</strong>
                  <span>{stat.label}</span>
                </div>
              ))}
            </motion.div>
          </section>
        </div>
      </section>
    </>
  );
};

export default Missions;
